import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { 
  Lightbulb, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Plus,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Target,
  Rocket,
  AlertTriangle,
  ExternalLink,
  Calendar,
  User,
  Tag,
  Edit3,
  Trash2,
  Link2,
  Bot,
  Loader2,
  Code,
  Database,
  Layout,
  Zap,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import DangerZoneButton from '../../components/admin/DangerZoneButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  'Proposed': { color: 'bg-blue-100 text-blue-800', icon: Lightbulb },
  'Planned': { color: 'bg-purple-100 text-purple-800', icon: Rocket },
  'In Progress': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  'Rejected': { color: 'bg-red-100 text-red-800', icon: XCircle },
};

const priorityConfig = {
  'Critical': { color: 'bg-red-500 text-white', icon: AlertTriangle },
  'High': { color: 'bg-orange-100 text-orange-800', icon: ArrowUp },
  'Medium': { color: 'bg-blue-100 text-blue-800', icon: Target },
  'Low': { color: 'bg-gray-100 text-gray-800', icon: ArrowDown },
};

export default function ProductRoadmapPage() {
  const { authToken } = useAuth();
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    status: 'Proposed',
    priority: 'Medium',
    category: ''
  });

  useEffect(() => {
    fetchRoadmap();
  }, []);

  const fetchRoadmap = async () => {
    try {
      const response = await fetch(`${API_URL}/api/roadmap`);
      const data = await response.json();
      setRoadmapItems(data);
    } catch (error) {
      console.error('Failed to fetch roadmap:', error);
      toast.error('Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProposal = async () => {
    if (!selectedItem || !authToken) return;
    
    setIsGeneratingProposal(true);
    try {
      const response = await fetch(`${API_URL}/api/support-agent/roadmap/${selectedItem.id}/generate-proposal`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update selected item with new proposal
        setSelectedItem(prev => ({ ...prev, ai_proposal: data.proposal }));
        // Update in the main list
        setRoadmapItems(prev => prev.map(item => 
          item.id === selectedItem.id ? { ...item, ai_proposal: data.proposal } : item
        ));
        toast.success('AI proposal generated!');
      } else {
        toast.error('Failed to generate proposal');
      }
    } catch (error) {
      console.error('Error generating proposal:', error);
      toast.error('Failed to generate proposal');
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/roadmap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem)
      });
      
      if (response.ok) {
        toast.success('Roadmap item added!');
        setIsAddDialogOpen(false);
        setNewItem({ title: '', description: '', status: 'Proposed', priority: 'Medium', category: '' });
        fetchRoadmap();
      }
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/roadmap/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Status updated!');
        fetchRoadmap();
        // Update selected item if open
        if (selectedItem?.id === itemId) {
          setSelectedItem(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (itemId, newPriority) => {
    try {
      const response = await fetch(`${API_URL}/api/roadmap/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority: newPriority })
      });
      
      if (response.ok) {
        toast.success('Priority updated!');
        fetchRoadmap();
        // Update selected item if open
        if (selectedItem?.id === itemId) {
          setSelectedItem(prev => ({ ...prev, priority: newPriority }));
        }
      }
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleOpenItem = (item) => {
    setSelectedItem(item);
    setIsDetailOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`${API_URL}/api/roadmap/${itemId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Item deleted');
        setIsDetailOpen(false);
        setSelectedItem(null);
        fetchRoadmap();
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const groupedItems = roadmapItems.reduce((acc, item) => {
    const status = item.status || 'Proposed';
    if (!acc[status]) acc[status] = [];
    acc[status].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div data-testid="product-roadmap-page">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-6 border-b border-slate-200 bg-white">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Rocket className="h-6 w-6 text-teal-600" />
            Product Roadmap
          </h1>
          <p className="text-gray-600 mt-1">
            Track upcoming features and enhancements for FedBD
          </p>
        </div>
        
        <div className="flex gap-2">
          <DangerZoneButton
            label="Clear Ticket-Derived Requests"
            testId="clear-ticket-derived-roadmap-btn"
            endpoint="/api/admin/danger/clear-ticket-derived-roadmap"
            confirmTitle="Clear ticket-derived roadmap items?"
            confirmDescription={
              <div>
                This deletes only <strong>roadmap rows that were auto-created from support tickets</strong>
                {' '}(rows where <code className="bg-slate-100 px-1 rounded font-mono text-xs">source_ticket_id</code> is set).
                <br /><br />
                <strong>Curated Features Roadmap items are preserved.</strong>
              </div>
            }
            onSuccess={() => fetchRoadmap()}
          />
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-roadmap-item-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-teal-600" />
                Add Roadmap Item
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Title *</label>
                <Input
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="Feature title..."
                  data-testid="roadmap-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <Textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Describe the feature..."
                  rows={3}
                  data-testid="roadmap-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Priority</label>
                  <Select
                    value={newItem.priority}
                    onValueChange={(value) => setNewItem({ ...newItem, priority: value })}
                  >
                    <SelectTrigger data-testid="roadmap-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Category</label>
                  <Input
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    placeholder="e.g., UX, API, etc."
                    data-testid="roadmap-category-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddItem} 
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="roadmap-submit-btn"
              >
                Add to Roadmap
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Main Content with Padding */}
      <div className="p-8 space-y-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-5 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const StatusIcon = config.icon;
          const count = groupedItems[status]?.length || 0;
          return (
            <Card key={status} className="border-l-4" style={{ borderLeftColor: status === 'Proposed' ? '#3B82F6' : status === 'Planned' ? '#8B5CF6' : status === 'In Progress' ? '#F59E0B' : status === 'Completed' ? '#10B981' : '#EF4444' }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{status}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <StatusIcon className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Kanban-style Board */}
      <div className="grid grid-cols-5 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const StatusIcon = config.icon;
          const items = groupedItems[status] || [];
          
          return (
            <div key={status} className="space-y-3">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.color}`}>
                <StatusIcon className="h-4 w-4" />
                <span className="font-medium">{status}</span>
                <Badge variant="secondary" className="ml-auto">{items.length}</Badge>
              </div>
              
              <div className="space-y-2 min-h-[200px]">
                {items.map((item) => {
                  const PriorityIcon = priorityConfig[item.priority]?.icon || Target;
                  return (
                    <Card 
                      key={item.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleOpenItem(item)}
                      data-testid={`roadmap-item-${item.id}`}
                    >
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start justify-between">
                          <h3 className="font-medium text-sm text-gray-900">{item.title}</h3>
                          <Badge className={priorityConfig[item.priority]?.color || 'bg-gray-100'}>
                            <PriorityIcon className="h-3 w-3 mr-1" />
                            {item.priority}
                          </Badge>
                        </div>
                        
                        {item.description && (
                          <p className="text-xs text-gray-500 line-clamp-2">{item.description}</p>
                        )}
                        
                        <div className="flex items-center gap-2 flex-wrap">
                          {item.category && (
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          )}
                          {item.source_ticket_id && (
                            <Badge variant="outline" className="text-xs text-purple-600 border-purple-200">
                              <Link2 className="h-3 w-3 mr-1" />
                              From Ticket
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {items.length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No items
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-teal-600" />
                  Roadmap Item Details
                </DialogTitle>
                <DialogDescription>
                  View and manage this roadmap item
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Title */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedItem.title}</h2>
                  {selectedItem.source_ticket_id && (
                    <Badge variant="outline" className="mt-2 text-purple-600 border-purple-200">
                      <Link2 className="h-3 w-3 mr-1" />
                      Created from Support Ticket
                    </Badge>
                  )}
                </div>

                {/* Description */}
                {selectedItem.description && (
                  <div className="space-y-2">
                    <Label className="text-gray-500">Description</Label>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedItem.description}</p>
                  </div>
                )}

                {/* Status & Priority Controls */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={selectedItem.status}
                      onValueChange={(value) => handleStatusChange(selectedItem.id, value)}
                    >
                      <SelectTrigger data-testid="detail-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Proposed">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-blue-500" />
                            Proposed
                          </div>
                        </SelectItem>
                        <SelectItem value="Planned">
                          <div className="flex items-center gap-2">
                            <Rocket className="h-4 w-4 text-purple-500" />
                            Planned
                          </div>
                        </SelectItem>
                        <SelectItem value="In Progress">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-yellow-500" />
                            In Progress
                          </div>
                        </SelectItem>
                        <SelectItem value="Completed">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            Completed
                          </div>
                        </SelectItem>
                        <SelectItem value="Rejected">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            Rejected
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={selectedItem.priority}
                      onValueChange={(value) => handlePriorityChange(selectedItem.id, value)}
                    >
                      <SelectTrigger data-testid="detail-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Critical">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            Critical
                          </div>
                        </SelectItem>
                        <SelectItem value="High">
                          <div className="flex items-center gap-2">
                            <ArrowUp className="h-4 w-4 text-orange-500" />
                            High
                          </div>
                        </SelectItem>
                        <SelectItem value="Medium">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-blue-500" />
                            Medium
                          </div>
                        </SelectItem>
                        <SelectItem value="Low">
                          <div className="flex items-center gap-2">
                            <ArrowDown className="h-4 w-4 text-gray-500" />
                            Low
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Category */}
                {selectedItem.category && (
                  <div className="space-y-2">
                    <Label className="text-gray-500">Category</Label>
                    <Badge variant="outline" className="text-sm">
                      <Tag className="h-3 w-3 mr-1" />
                      {selectedItem.category}
                    </Badge>
                  </div>
                )}

                {/* AI Implementation Proposal */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2 text-purple-700">
                      <Bot className="h-4 w-4" />
                      AI Implementation Proposal
                    </Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateProposal}
                      disabled={isGeneratingProposal}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      data-testid="generate-proposal-btn"
                    >
                      {isGeneratingProposal ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : selectedItem.ai_proposal ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Regenerate
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Proposal
                        </>
                      )}
                    </Button>
                  </div>

                  {selectedItem.ai_proposal ? (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                      {/* Summary */}
                      <div>
                        <p className="text-gray-700 text-sm">{selectedItem.ai_proposal.summary}</p>
                      </div>

                      {/* Effort & Suggested Priority */}
                      <div className="flex gap-3 flex-wrap">
                        {selectedItem.ai_proposal.effort_estimate && (
                          <Badge className="bg-blue-100 text-blue-800">
                            <Clock className="h-3 w-3 mr-1" />
                            {selectedItem.ai_proposal.effort_estimate}
                          </Badge>
                        )}
                        {selectedItem.ai_proposal.suggested_priority && (
                          <Badge className="bg-orange-100 text-orange-800">
                            <Target className="h-3 w-3 mr-1" />
                            AI suggests: {selectedItem.ai_proposal.suggested_priority}
                          </Badge>
                        )}
                      </div>

                      {/* Components */}
                      {selectedItem.ai_proposal.components?.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs text-gray-500 uppercase">Components to Build</Label>
                          <div className="space-y-1">
                            {selectedItem.ai_proposal.components.map((comp, idx) => {
                              const typeIcons = {
                                frontend: Layout,
                                backend: Code,
                                database: Database,
                                integration: Zap
                              };
                              const TypeIcon = typeIcons[comp.type] || Code;
                              return (
                                <div key={idx} className="flex items-start gap-2 text-sm bg-white p-2 rounded border">
                                  <TypeIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-medium text-gray-800">{comp.name}</span>
                                    <span className="text-gray-500"> - {comp.description}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Technical Approach */}
                      {selectedItem.ai_proposal.technical_approach && (
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500 uppercase">Technical Approach</Label>
                          <p className="text-sm text-gray-600 bg-white p-2 rounded border">
                            {selectedItem.ai_proposal.technical_approach}
                          </p>
                        </div>
                      )}

                      {/* Dependencies & Risks */}
                      <div className="grid grid-cols-2 gap-3">
                        {selectedItem.ai_proposal.dependencies?.length > 0 && (
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500 uppercase">Dependencies</Label>
                            <ul className="text-xs text-gray-600 space-y-1">
                              {selectedItem.ai_proposal.dependencies.map((dep, idx) => (
                                <li key={idx} className="flex items-start gap-1">
                                  <span className="text-purple-400">•</span> {dep}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedItem.ai_proposal.risks?.length > 0 && (
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500 uppercase">Risks</Label>
                            <ul className="text-xs text-gray-600 space-y-1">
                              {selectedItem.ai_proposal.risks.map((risk, idx) => (
                                <li key={idx} className="flex items-start gap-1">
                                  <AlertTriangle className="h-3 w-3 text-orange-400 flex-shrink-0 mt-0.5" /> {risk}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Generated timestamp */}
                      {selectedItem.ai_proposal.generated_at && (
                        <div className="text-xs text-gray-400 pt-2 border-t">
                          Generated: {new Date(selectedItem.ai_proposal.generated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Bot className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        Click "Generate Proposal" to have AI analyze this feature and suggest an implementation plan.
                      </p>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  {selectedItem.created_at && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      Created: {new Date(selectedItem.created_at).toLocaleDateString()}
                    </div>
                  )}
                  {selectedItem.votes > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <ArrowUp className="h-4 w-4" />
                      {selectedItem.votes} vote{selectedItem.votes !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex justify-between">
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(selectedItem.id)}
                  data-testid="detail-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
